import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiService } from '@/services/api'
import { hubspotApi } from '@/services/hubspot'
import { useSignupFirstKnown } from '@/hooks/useSignupFirst'
import { normalizeDomainForMatching } from '@/utils/domain'
import type { ReportConnection } from '@/components/welcome/HubSpotReport'
import type { HubPagesList, PageFactsSnapshot, PutLiveJob } from '@/types/hubspotOnboarding'

/**
 * Everything the HubSpot report needs, in one place: the facts (refreshed
 * until the picture arrives), whether a connected portal serves this site,
 * and the put-live job polled to its end.
 *
 * `active` is false unless the SERVER says the flag is on and the site is on
 * HubSpot, so with the flag off the results page behaves exactly as before.
 * `loading` is true until the server has answered once; a page that is still
 * deciding waits rather than starting the old flow underneath the new one.
 */

export interface HubSpotOnboardingReturnParams {
  connected?: string | null
  matched?: string | null
  portal?: string | null
}

/** The server's answer to "is this account's first page free for this page". `reason` is its own word for a no, when it has one. */
export interface FreePageAnswer {
  eligible: boolean
  reason: string | null
}

/** No answer is a no: the person goes to the trial, which is where they went before there was a free page. */
const NO_FREE_PAGE: FreePageAnswer = { eligible: false, reason: null }

export interface HubSpotOnboardingOptions {
  /**
   * Whether to ask the server if the first page is free. The free page is
   * for an account with no plan, so the page that knows the plan says false
   * for an account that has one, and until it knows: each question costs the
   * server its reads, and an account with a plan has no use for the answer.
   * While it is false nothing is asked and `freePage` is null. Left out, it
   * is true.
   */
  askFreePage?: boolean
}

export interface HubSpotOnboardingState {
  active: boolean
  loading: boolean
  /**
   * True while the facts are here but the picture is not, and we are still
   * asking for it. The report waits on this: the picture is its payoff, and
   * a report that appears with an empty frame and fills it in later reads
   * as broken. After the polling budget it goes false and the report shows
   * without the picture rather than never.
   */
  awaitingPicture: boolean
  /** The picture has been on its way for longer than a person should wait without being told. */
  pictureSlow: boolean
  /** The person's own choice to see the fix without its picture. */
  showWithoutPicture: () => void
  facts: PageFactsSnapshot | null
  connection: ReportConnection
  job: PutLiveJob | null
  /**
   * A sentence for when putting the page live could not even start, so there
   * is no job to draw a pipeline from. The one everybody meets is "no plan
   * yet": the card was never taken, and the answer is the dialog, not a retry.
   */
  putLiveError: string | null
  /**
   * True from the ask until the job is in hand. Returning from the card the
   * report has no job yet for a round trip, and without this it drew the
   * "Put it live" card the person had just pressed.
   */
  putLiveStarting: boolean
  /**
   * True once the look for a job on arrival has come back for this scan,
   * whatever it found: a job, none, or no answer at all (a look that failed
   * has still come back). When it found a job, the job is in hand in the
   * same draw this turns true. A press made by the page itself waits on it:
   * before it, `putLive` cannot know the scan has a job, and would be for
   * the page the report shows when the server takes only the job's own.
   * False with no claimed scan, and while the report is not this one's.
   */
  jobLookupSettled: boolean
  /**
   * Starts the job, or tries it again. A first press is for the page the
   * report shows; once the scan has a job, a press is for the page that job
   * was started on, which is the only one the server takes for it.
   * `plan_required` is the server saying there is no plan and no free page:
   * the page takes the person to the trial rather than showing a sentence
   * about it, and `freePage` then holds the server's reason. A press for a
   * page chosen that is refused that way looks once for a job on the scan
   * first: a tab that is behind is then shown the job, and answered
   * `refused`, not sent to a trial.
   */
  putLive: () => Promise<'started' | 'plan_required' | 'refused'>
  /** Reads the live page again for a fix that is on HubSpot but was not confirmed yet. */
  confirmAgain: () => Promise<void>
  /** True from the ask until the confirm job is in hand. */
  confirming: boolean
  refreshConnection: () => Promise<void>
  /** The connected account's pages, once read; null until then or when they could not be read. */
  hubPages: HubPagesList | null
  hubPagesLoading: boolean
  /**
   * Whether the first page is free for this account and this scan's page.
   * Null while there is no answer. The server is asked once per scan per
   * load of the report, and only when the card that says the answer is
   * what the report draws: a portal is matched, and the scan has no job, or
   * its job stopped while the fix was being written. Never polled.
   *
   * A job that stopped while the fix was being written is asked about
   * because it puts the Put it live card back, and after a reload that card
   * could never say whether its press is free. The server answers that
   * about the job's own page. A job the publish step refused is not: the
   * report draws that refusal, with no line about what a press costs, and
   * its Try again goes straight to the server.
   *
   * So it is null, and stays null: while the caller says not to ask
   * (`askFreePage` false: an account with a plan, or one whose plan is not
   * known yet), while no portal is matched, and for a scan whose job is
   * running, has published, or was refused at the publish step, where the
   * report draws the job. Null here never means no, and never by itself
   * means an answer is coming: `freePagePending` says that.
   *
   * A request that failed or ran out of time, or an answer that cannot be
   * read, is a no with no reason: the ordinary path, with nothing said
   * about it. Where sign-up does not come first nothing is asked and the
   * answer is that same no.
   *
   * A press the server refuses for want of a plan replaces it: the server
   * can say yes here and no a moment later (another press won, the day's
   * pages ran out), and its no is the newer word. `reason` is then the one
   * the refusal named, or null when it named none.
   */
  freePage: FreePageAnswer | null
  /**
   * True while an answer to "is the first page free" is still to come for
   * this scan: the question is out, or it will be put as soon as the two
   * things it waits for are known (whether sign-up comes first, and whether
   * the scan has a job). The card under Put it live says nothing about what
   * a press leads to while this is true: the press may yet be the free one,
   * and a line that promised a trial would be wrong a moment later.
   *
   * False wherever no answer is coming: no portal matched, the caller says
   * not to ask (a plan, or a plan not known yet), sign-up does not come
   * first, a job that is running, has published or was refused at the
   * publish step, and while a press is on its way. False once there is an
   * answer, which a request that failed or ran out of time also is.
   *
   * It always ends. The question gives up after the API's own time limit
   * and is then a no; the look for a job comes back even when it fails; and
   * whether sign-up comes first settles even when the server does not say.
   */
  freePagePending: boolean
  /**
   * True while the fix can be put on another page the scan read, in place
   * of the one the report picked: the report is this scan's HubSpot report,
   * the look for a job on arrival has come back and found none, no job has
   * been seen for the scan since, and no press is on its way. Once any job
   * has been seen for the scan (found on arrival, started here, heard of
   * while a press was on its way, or told by the server answering another
   * page than the one chosen, however it ended, and after it is let go of)
   * this is false for good: a job is for one page, and the server takes no
   * other for it. It stays true while a choice is being read
   * (`choosingPage`): the offer stands, and a second choice is ignored.
   */
  canChoosePage: boolean
  /**
   * Puts the fix on another page the scan read. `url` is one of
   * `facts.pages[].url`, exactly as the server sent it. One request: the
   * facts for that page, which replace the report's when they arrive. The
   * choice is kept for the tab and this scan, so it is still the page after
   * a reload and after the trip through checkout, and a press is for it.
   *
   * Ignored unless `canChoosePage`, while another choice is being read, and
   * for the page the report already shows. The server has the last word:
   * when it answers another page (the scan has a job by now) the choice is
   * forgotten, what came back is shown and no choice is offered again; when it says the scan never read
   * that page the choice is forgotten, `putLiveError` holds its sentence
   * and the facts are asked for again with no page; when the request fails
   * any other way the report stays as it was, the choice is forgotten and
   * `putLiveError` says so.
   */
  choosePage: (url: string) => void
  /** The address of a chosen page whose facts are being read, from the choice until the answer. Null otherwise, and on a load that starts from a remembered choice: nothing is on screen to choose from then. */
  choosingPage: string | null
  /**
   * True while a page the person chose is in place for this scan: from the
   * moment a choice is made here, or from the first draw when one was
   * remembered for the scan. False once the choice is let go of (the scan
   * has a fix, or the server did not take the page) and for any other scan.
   * The report stops saying we picked the page while this is true.
   */
  pageChosen: boolean
}

/*
  The report is never shown without its picture. The picture is the payoff,
  and a frame that says one is on its way is the report looking broken.
  Polling has no budget: the server tries the picture again on every read
  once an attempt has failed. What a person gets after a long wait is a
  choice, not the empty frame: after PICTURE_SLOW_MS the waiting screen
  offers to show the fix without it, and only their press does that.
*/
const CAPTURE_POLL_MS = 4000
export const PICTURE_SLOW_MS = 4 * 60 * 1000
/*
  How many times the facts are asked for when a report starts from a
  remembered choice and gets no answer, CAPTURE_POLL_MS apart. One read that
  fails must not be the end of it: nothing is on screen yet, the page would
  go to the older report without a word, and the press the way back from
  checkout makes would never come. Three, because every request that names a
  page costs the server a read of the whole scan.
*/
const REMEMBERED_PAGE_TRIES = 3

/*
  The last complete snapshot, kept for the page's return. Connecting HubSpot
  leaves the page and comes back to it, and the report showed "Finalizing
  your report" all over again for the second it took to ask for facts it had
  already shown. Hydrating from the last snapshot renders the report at once;
  the fetch still runs and replaces it. Session-scoped and per scan, and only
  a snapshot with its picture is kept, so a hydrated report is never one that
  is still waiting.
*/
const snapshotKey = (claimedScanId: string) => `hubspot_report_${claimedScanId}`

export function readReportSnapshot(claimedScanId: string | null | undefined): PageFactsSnapshot | null {
  if (!claimedScanId) return null
  try {
    const raw = sessionStorage.getItem(snapshotKey(claimedScanId))
    const parsed = raw ? (JSON.parse(raw) as PageFactsSnapshot) : null
    return isActiveSnapshot(parsed) && parsed.captureUrl ? parsed : null
  } catch {
    return null
  }
}

function writeSnapshot(claimedScanId: string, facts: PageFactsSnapshot): void {
  try {
    sessionStorage.setItem(snapshotKey(claimedScanId), JSON.stringify(facts))
  } catch {
    /* storage may be unavailable; the report still loads, with its second of waiting */
  }
}

/*
  The page the person chose in place of ours, kept beside the snapshot: for
  the tab, and per scan. It has to outlive a reload and the trip to the
  trial and back, because the page presses Put it live by itself on that
  return, and without this the fix would go onto our pick after the person
  had chosen, and been shown, another page. Only the address is kept. The
  snapshot above stays the page the report itself arrives at (our pick, or
  the page of the scan's job), so a report with no choice starts as it
  always did.
*/
const chosenPageKey = (claimedScanId: string) => `hubspot_report_page_${claimedScanId}`

export function readChosenPage(claimedScanId: string | null | undefined): string | null {
  if (!claimedScanId) return null
  try {
    return sessionStorage.getItem(chosenPageKey(claimedScanId)) || null
  } catch {
    return null
  }
}

function writeChosenPage(claimedScanId: string, url: string | null): void {
  try {
    if (url) sessionStorage.setItem(chosenPageKey(claimedScanId), url)
    else sessionStorage.removeItem(chosenPageKey(claimedScanId))
  } catch {
    /* storage may be unavailable; the choice still holds for this visit */
  }
}
const JOB_POLL_MS = 2000

const START_TRIAL_SENTENCE = 'Start your trial to put this page live.'
const COULD_NOT_START_SENTENCE = 'We could not start on this page. Try again in a moment.'
const LOST_TRACK_SENTENCE = 'We lost track of this page. Reload to see where it got to.'
const COULD_NOT_CHECK_SENTENCE = 'We could not read your page just now. Try again in a moment.'
/* The server's own words for a page the scan did not read, for the answer that carries none. */
const PAGE_NOT_READ_SENTENCE = 'That page is not one this scan read.'

/**
 * A poll can land on the API instance that has not yet read the snapshot a
 * sibling instance just wrote, right after `putLive` starts a job, and see a
 * false "not started". Give that a handful of normal-interval retries before
 * treating it as real.
 */
const NOT_STARTED_RETRY_LIMIT = 5

const FALLBACK_PORTAL_NAME = 'Your HubSpot account'

/** The same bar the put-live job holds HubSpot's matcher to before it publishes. */
const HUB_MATCH_CONFIDENCE = 0.7

type TeamConnectionRow = NonNullable<Awaited<ReturnType<typeof hubspotApi.getTeamConnections>>['data']>[number]

/**
 * Whether these facts are a report to draw. The HubSpot flow has to be on.
 * Then either the scan read the site as HubSpot, or the server runs sign-up
 * first, where a claimed scan is a report whatever its home page read as:
 * that read is one fetch, and whether the site is in the person's HubSpot
 * account is decided where they connect it. A snapshot kept from before the
 * server said which it runs under has no `signupFirst`, and the platform
 * decides for it as it always did.
 */
function isActiveSnapshot(facts: PageFactsSnapshot | null): facts is PageFactsSnapshot {
  return Boolean(facts && facts.hubspotOnly && (facts.platform === 'hubspot' || facts.signupFirst === true))
}

/**
 * What the server said when a request was refused, if it said anything a
 * person may read. `reason` is its word for why a free first page was not
 * given; most refusals carry none.
 */
function refusal(error: unknown): { status: number | null; code: string | null; message: string | null; reason: string | null } {
  const response = (error as { response?: { status?: number; data?: { error?: unknown; message?: unknown; reason?: unknown } } } | null)?.response
  const data = response?.data
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    code: typeof data?.error === 'string' ? data.error : null,
    message: typeof data?.message === 'string' && data.message.trim() ? data.message : null,
    reason: typeof data?.reason === 'string' && data.reason ? data.reason : null,
  }
}

/** The server's answer as it can be trusted: only a plain yes is a yes, and anything that is not an answer is a no. */
function readFreePageAnswer(data: unknown): FreePageAnswer {
  if (typeof data !== 'object' || data === null) return NO_FREE_PAGE
  const { eligible, reason } = data as { eligible?: unknown; reason?: unknown }
  if (eligible === true) return { eligible: true, reason: null }
  if (eligible === false) return { eligible: false, reason: typeof reason === 'string' && reason ? reason : null }
  return NO_FREE_PAGE
}

export function useHubSpotOnboarding(
  claimedScanId: string | null | undefined,
  domain: string | null | undefined,
  returnParams: HubSpotOnboardingReturnParams,
  options: HubSpotOnboardingOptions = {}
): HubSpotOnboardingState {
  const askFreePage = options.askFreePage !== false
  /* The remembered snapshot is the report's own page. With a choice remembered it is the wrong page to paint, so the report waits for the right one. */
  const [facts, setFacts] = useState<PageFactsSnapshot | null>(() => (readChosenPage(claimedScanId) ? null : readReportSnapshot(claimedScanId)))
  const [loading, setLoading] = useState(() => readChosenPage(claimedScanId) !== null || readReportSnapshot(claimedScanId) === null)
  /*
    The page the person chose in place of ours, for the scan it was chosen
    on: what the facts are asked for, and what a press is for until the scan
    has a job. Read from the tab when the report for a scan starts, set by a
    choice, and let go of for good once the scan has a job or the server did
    not take it. Kept where the same tick can read it: a press made straight
    after a choice is for the page chosen.
  */
  const chosen = useRef<{ scanId: string; url: string } | null>(null)
  /* A choice whose facts are being read, as last set and as drawn. */
  const choosing = useRef<string | null>(null)
  const [choosingPage, setChoosingPage] = useState<string | null>(null)
  /* The scan a choice is in place for, as drawn. */
  const [chosenFor, setChosenFor] = useState<string | null>(() => (claimedScanId && readChosenPage(claimedScanId) ? claimedScanId : null))
  /* The request for the facts that counts. An answer to an older one is dropped. */
  const factsRun = useRef(0)
  /* Asks for the facts again, for the page chosen if there is one. Set while the report for a scan is loading or loaded. */
  const askFacts = useRef<(() => void) | null>(null)
  /* The scan the facts were last asked for, so leaving it can forget its choice. */
  const factsScan = useRef<string | null>(null)
  const [awaitingPicture, setAwaitingPicture] = useState(false)
  const [pictureSlow, setPictureSlow] = useState(false)
  const waivedPicture = useRef(false)
  const holding = useRef(false)
  const slowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [connection, setConnection] = useState<ReportConnection>({ state: 'not_connected' })
  const [job, setJobState] = useState<PutLiveJob | null>(null)
  /* The job as last set, readable in the same tick: the arrival lookup below
     must not overwrite a job this page has just started. */
  const jobRef = useRef<PutLiveJob | null>(null)
  /*
    The page the scan's job was started on, kept once any job has been seen
    and after the job itself is let go of. A press for a scan that has a job
    is for that page: the report's own pick is read from the live site again
    and can move, and the server takes no other address for a second try.
  */
  const jobPage = useRef<{ scanId: string; url: string } | null>(null)
  const setJob = useCallback((next: PutLiveJob | null) => {
    jobRef.current = next
    if (next && typeof next.url === 'string' && next.url) jobPage.current = { scanId: next.claimedScanId, url: next.url }
    setJobState(next)
  }, [])
  const [putLiveStarting, setPutLiveStarting] = useState(false)
  const startingRef = useRef(false)
  /*
    The scan a job has been seen for, by any road: found on arrival, heard
    of while a press was on its way, started here, polled. Kept after the
    job itself is let go of. From then on the page is the job's for good,
    and no other can be chosen.
  */
  const jobSeen = useRef<string | null>(null)
  const [jobSeenFor, setJobSeenFor] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [hubPages, setHubPages] = useState<HubPagesList | null>(null)
  /* The page the account's pages were matched against, so a match made for another page is not taken for this one's. */
  const [hubPagesFixUrl, setHubPagesFixUrl] = useState<string | null>(null)
  const [hubPagesLoading, setHubPagesLoading] = useState(false)
  const [putLiveError, setPutLiveError] = useState<string | null>(null)
  const signupFirst = useSignupFirstKnown()
  /* The scan whose job has been looked for on arrival, whatever came back. */
  const [jobLookedFor, setJobLookedFor] = useState<string | null>(null)
  const [freePageAnswer, setFreePageAnswer] = useState<{ scanId: string; answer: FreePageAnswer } | null>(null)
  /*
    The question as last put for a scan, by asking or by a press the server
    refused. One per load of the report: while this names the scan nothing
    is asked again, and an answer that comes back to a question that is no
    longer the one held here is the older word and is dropped.
  */
  const freePageQuestion = useRef<{ scanId: string } | null>(null)
  const captureTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const jobTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notStartedStreak = useRef(0)

  const active = isActiveSnapshot(facts)

  const { connected, matched, portal } = returnParams
  const returnSaysMatched = connected === '1' && matched === '1'
  const returnSaysWrongPortal = connected === '1' && matched === '0'

  /* The choice is let go of: in hand, in the tab, and on the list that was waiting on it. */
  const forgetChoice = useCallback((scanId: string) => {
    if (chosen.current?.scanId === scanId) chosen.current = null
    writeChosenPage(scanId, null)
    choosing.current = null
    setChoosingPage(null)
    setChosenFor((held) => (held === scanId ? null : held))
  }, [])

  /*
    A job has been seen for the scan. The page is the job's from here on,
    so a choice in hand ends: the server answers the job's page whatever is
    named, a press is for that page, and nothing may put a second page of
    the scan under way. The facts are then asked for with no page, which is
    the job's page, and the report is not held for a picture: it is on
    screen, with the person watching the fix go on.
  */
  const sawJob = useCallback((scanId: string) => {
    const held = chosen.current?.scanId === scanId
    if (jobSeen.current === scanId && !held) return
    jobSeen.current = scanId
    setJobSeenFor(scanId)
    forgetChoice(scanId)
    if (held) {
      waivedPicture.current = true
      askFacts.current?.()
    }
  }, [forgetChoice])

  useEffect(() => {
    if (!claimedScanId) return
    let cancelled = false
    waivedPicture.current = false
    holding.current = false
    setPictureSlow(false)
    /* A different scan is a different choice: the one left behind is forgotten, and this scan's own is read from the tab. */
    if (factsScan.current && factsScan.current !== claimedScanId) writeChosenPage(factsScan.current, null)
    factsScan.current = claimedScanId
    const remembered = readChosenPage(claimedScanId)
    chosen.current = remembered ? { scanId: claimedScanId, url: remembered } : null
    setChosenFor(remembered ? claimedScanId : null)
    choosing.current = null
    setChoosingPage(null)
    /* Reads of a remembered page that got no answer, for this scan's report. */
    let rememberedTries = 0
    const load = async (run: number) => {
      const asked = chosen.current?.scanId === claimedScanId ? chosen.current.url : null
      /* Chosen from the report on this visit: the report is on screen, and stays there whatever the answer. */
      const fromReport = asked !== null && choosing.current === asked
      try {
        const response = asked
          ? await apiService.getClaimedOnboardingFacts(claimedScanId, asked)
          : await apiService.getClaimedOnboardingFacts(claimedScanId)
        if (cancelled || run !== factsRun.current) return
        const data = response.data ?? null
        /*
          The answer names its own page. The page asked for: the report is
          about that page now. Any other: the scan has a job by now, the
          server answered the job's page, and the choice was not taken.
          That is a job seen for the scan, as much as one found by the look
          on arrival: the look may have got no answer, and without this the
          choice stayed on offer, every press of it asked the server, and
          each answer was the job's page again with nothing said.
        */
        const taken = asked !== null && data?.url === asked
        if (asked !== null) {
          if (taken) {
            choosing.current = null
            setChoosingPage(null)
          } else {
            jobSeen.current = claimedScanId
            setJobSeenFor(claimedScanId)
            forgetChoice(claimedScanId)
          }
          if (taken || fromReport) waivedPicture.current = true
        }
        setFacts(data)
        setLoading(false)
        /* Only the report's own page is kept as its snapshot, never a page chosen. */
        if (isActiveSnapshot(data) && data.captureUrl && !taken) writeSnapshot(claimedScanId, data)
        // The picture lands after the facts; keep asking, and hold the
        // report until it is there or the person chooses to go without.
        // Not for a page the person chose: it was chosen from a list with
        // no picture in it, it shows at once, and it is asked for once.
        // Every request that names a page costs the server a read of the
        // whole scan.
        if (isActiveSnapshot(data) && !data.captureUrl && !taken) {
          holding.current = true
          if (!waivedPicture.current) setAwaitingPicture(true)
          if (!slowTimer.current) slowTimer.current = setTimeout(() => { if (!cancelled) setPictureSlow(true) }, PICTURE_SLOW_MS)
          captureTimer.current = setTimeout(() => { if (!cancelled && run === factsRun.current) void load(run) }, CAPTURE_POLL_MS)
        } else {
          holding.current = false
          setAwaitingPicture(false)
          setPictureSlow(false)
        }
      } catch (error) {
        if (cancelled || run !== factsRun.current) return
        if (asked !== null) {
          const { code, message } = refusal(error)
          if (code === 'PAGE_NOT_READ') {
            /*
              The scan did not read that page. The choice is forgotten, the
              server's sentence is said as sent, and the facts are asked
              for again with no page. A report still loading keeps loading
              until they are here: one that stopped with nothing to show
              would hand the page to the old flow.
            */
            forgetChoice(claimedScanId)
            if (fromReport) waivedPicture.current = true
            setPutLiveError(message ?? PAGE_NOT_READ_SENTENCE)
            ask()
            return
          }
          /*
            No answer, or one that says nothing about the page. Chosen from
            the report: the report stays as it was, the choice is forgotten
            and the person is told. It is not asked for again.
          */
          if (fromReport) {
            forgetChoice(claimedScanId)
            setPutLiveError(message || COULD_NOT_CHECK_SENTENCE)
          } else {
            /*
              A remembered choice that got no answer on a load, with no
              report on screen yet. It is not forgotten: the server never
              refused it, and falling back to our pick would let the way
              back from checkout put the fix on a page the trial did not
              name. It is asked for again, and the report keeps loading
              meanwhile. The wait is the picture's own timer, so leaving
              the page or the scan ends it, and so does a job seen for the
              scan, which asks for the facts afresh.
            */
            rememberedTries += 1
            if (rememberedTries < REMEMBERED_PAGE_TRIES) {
              captureTimer.current = setTimeout(() => { if (!cancelled && run === factsRun.current) void load(run) }, CAPTURE_POLL_MS)
              return
            }
            /*
              No answer, three times. The report's own page is shown as the
              tab last kept it, with the sentence, in place of nothing: with
              nothing to show the page draws the older report and says
              nothing went wrong. The choice is still kept, so a press is
              for the page chosen, and once its job starts the facts are
              asked for again and the report is about that page.
            */
            const kept = readReportSnapshot(claimedScanId)
            if (kept) {
              setFacts(kept)
              setPutLiveError(message || COULD_NOT_CHECK_SENTENCE)
            }
          }
        }
        setLoading(false)
        // A poll that fails while the picture is on its way is not the
        // picture failing: ask again rather than let the empty frame through.
        if (holding.current) captureTimer.current = setTimeout(() => { if (!cancelled && run === factsRun.current) void load(run) }, CAPTURE_POLL_MS)
        else setAwaitingPicture(false)
      }
    }
    /* A new request for the facts. Whatever was out, or waiting to go out, is the older word from here on. */
    const ask = () => {
      if (captureTimer.current) {
        clearTimeout(captureTimer.current)
        captureTimer.current = null
      }
      factsRun.current += 1
      void load(factsRun.current)
    }
    askFacts.current = ask
    ask()
    return () => {
      cancelled = true
      askFacts.current = null
      if (captureTimer.current) {
        clearTimeout(captureTimer.current)
        captureTimer.current = null
      }
      if (slowTimer.current) {
        clearTimeout(slowTimer.current)
        slowTimer.current = null
      }
    }
  }, [claimedScanId, forgetChoice])

  const showWithoutPicture = useCallback(() => {
    waivedPicture.current = true
    setAwaitingPicture(false)
  }, [])

  const refreshConnection = useCallback(async () => {
    if (!domain) return
    const wanted = normalizeDomainForMatching(domain)
    try {
      const response = await hubspotApi.getTeamConnections()
      const rows: TeamConnectionRow[] = response?.data ?? []
      // The same rule the server applies: exact host, scheme, path and www set aside.
      const row = rows.find((r) =>
        r.isActive && (r.associatedDomains ?? []).some((d: string) => normalizeDomainForMatching(d) === wanted)
      )
      if (row || returnSaysMatched) {
        setConnection({
          state: 'matched',
          connectionId: row?.id ?? null,
          portalName: row?.portalName || portal || FALLBACK_PORTAL_NAME,
          matchedDomain: domain,
          pagesOnPortal: null,
        })
        return
      }
      setConnection({ state: returnSaysWrongPortal ? 'wrong_portal' : 'not_connected' })
    } catch {
      // No answer from the list is not a yes. The return from HubSpot is the
      // server's own word, so that still counts.
      if (returnSaysMatched) {
        setConnection({ state: 'matched', connectionId: null, portalName: portal || FALLBACK_PORTAL_NAME, matchedDomain: domain, pagesOnPortal: null })
      } else if (returnSaysWrongPortal) {
        setConnection({ state: 'wrong_portal' })
      }
    }
  }, [domain, portal, returnSaysMatched, returnSaysWrongPortal])

  useEffect(() => {
    if (!active) return
    void refreshConnection()
  }, [active, refreshConnection])

  /*
    After connect the report shows the account's pages with the fix page at
    the top, so the person sees the fix going onto a page HubSpot lists,
    not onto a picture. HubSpot's own matcher names the page; the list is
    everything the account publishes.
  */
  const connectionId = connection.state === 'matched' ? connection.connectionId : null
  const fixUrl = facts?.url ?? null
  useEffect(() => {
    if (!connectionId || !fixUrl) return
    let cancelled = false
    setHubPagesLoading(true)
    void (async () => {
      try {
        const [listed, matched] = await Promise.all([
          hubspotApi.listAllPages(connectionId),
          hubspotApi.matchContent(connectionId, fixUrl).catch(() => ({ data: [] })),
        ])
        if (cancelled) return
        const rows: Array<{ id: string; name: string; url: string; state: string }> = listed?.data ?? []
        const best = (matched?.data ?? [])[0]
        setHubPages({
          pages: rows.map((row) => ({ id: row.id, title: row.name, url: row.url, published: row.state === 'PUBLISHED' })),
          fixId: best && best.confidence >= HUB_MATCH_CONFIDENCE ? best.contentId : null,
          total: rows.length,
        })
        setHubPagesFixUrl(fixUrl)
      } catch {
        if (!cancelled) setHubPages(null)
      } finally {
        if (!cancelled) setHubPagesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [connectionId, fixUrl])
  /*
    HubSpot's match names the row of the page the report showed when it was
    made. Once the report is about another page, and until the account's
    pages have been matched to that one, the match in hand would mark the
    page left behind as the fix's and list the page chosen under it. So it
    names no row meanwhile, and the report tells the fix's row by its
    address.
  */
  const hubPagesNow = useMemo(
    () => (hubPages && hubPages.fixId !== null && hubPagesFixUrl !== fixUrl ? { ...hubPages, fixId: null } : hubPages),
    [hubPages, hubPagesFixUrl, fixUrl]
  )

  const pollJob = useCallback(async () => {
    if (!claimedScanId) return
    try {
      const response = await apiService.getOnboardingPutLive(claimedScanId)
      const next = response.data ?? null
      notStartedStreak.current = 0
      setJob(next)
      if (next) sawJob(claimedScanId)
      if (next && !next.finishedAt) {
        jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
      }
    } catch (error) {
      // A job that was started can briefly look "not started" if this poll
      // lands on an instance that has not read the other one's snapshot yet.
      // Give that a few normal-interval retries before believing it.
      if (refusal(error).code === 'NOT_STARTED') {
        notStartedStreak.current += 1
        if (notStartedStreak.current >= NOT_STARTED_RETRY_LIMIT) {
          setPutLiveError(LOST_TRACK_SENTENCE)
          return
        }
        jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
        return
      }
      jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
    }
  }, [claimedScanId, sawJob, setJob])

  /*
    A job already under way or finished, asked for on arrival. The page used
    to learn of a job only by starting one, so a reload, a tab the browser
    had put to sleep, or the way back from the dashboard lost the stage and
    showed the report from before the trial, "Put it live" and all. The
    server keeps the job on the scan row at every step; this reads it.
  */
  useEffect(() => {
    if (!claimedScanId || !active) return
    let cancelled = false
    void (async () => {
      try {
        const response = await apiService.getOnboardingPutLive(claimedScanId)
        if (cancelled) return
        const found = response.data ?? null
        // The page the scan's job was started on is kept whatever comes next,
        // and before anything can return: a press on its way when this lands
        // (the way back from the trial presses by itself) keeps the job out
        // of hand, and the press after it must still be for the job's page.
        if (found && typeof found.url === 'string' && found.url) jobPage.current = { scanId: claimedScanId, url: found.url }
        // And so is the fact that the scan has a job at all, which ends any choice of another page.
        if (found) sawJob(claimedScanId)
        // A job this page started meanwhile is newer than what was asked for.
        // The job is in hand before the look is marked as made, so nothing
        // ever sees "looked, and no job" for a scan that has one.
        if (found && !jobRef.current && !startingRef.current) {
          setJob(found)
          if (!found.finishedAt) jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
        }
        setJobLookedFor(claimedScanId)
      } catch {
        // Nothing started for this page, or no answer: the report stands as it is.
        if (!cancelled) setJobLookedFor(claimedScanId)
      }
    })()
    return () => { cancelled = true }
  }, [claimedScanId, active, pollJob, sawJob, setJob])

  /*
    Is the first page free? Asked of the server once per scan per load, and
    only when the card that says the answer is what the report draws: a
    portal is matched, the look for a job on arrival has come back, no press
    is on its way, and the scan has no job in hand or its job stopped while
    the fix was being written. A job that is running or has published is
    what the report draws, and the press answers that job whatever this
    would say. A job that stopped while the fix was being written puts the
    Put it live card back, and without an answer that card could never say
    whether the press is free. A job the publish step refused does not: the
    report draws the refusal (the same test the report makes: an error, and
    the publish step needing the person), nothing there says what a press
    costs, and Try again goes straight to the server, so an answer would be
    read by nobody. The answer can cost the
    server its reads, so it is never polled and nothing brings it round
    again: not the picture's polling, not the connection being read again,
    not a press, not a job ending a second time. Not asked until it is known
    that sign-up comes first, and not at all where it does not: the server's
    answer there is always no. Not asked while the caller says not to: an
    account with a plan has no use for the answer.
  */
  const portalMatched = connection.state === 'matched'
  const refusedAtPublish = Boolean(job?.error && job.steps.published === 'needs-you')
  /* The Put it live card is what the report draws, or will be: no job, or one that stopped before anything reached HubSpot. */
  const cardOnOffer = (job === null || (Boolean(job.finishedAt) && !job.deploymentId && !refusedAtPublish)) && !putLiveStarting
  const canAsk = Boolean(claimedScanId) && jobLookedFor === claimedScanId && cardOnOffer
  useEffect(() => {
    if (!askFreePage || !claimedScanId || !active || !portalMatched || signupFirst !== true || !canAsk) return
    if (freePageQuestion.current?.scanId === claimedScanId) return
    const question = { scanId: claimedScanId }
    freePageQuestion.current = question
    void (async () => {
      let answer = NO_FREE_PAGE
      try {
        const response = await apiService.getOnboardingFreePage(claimedScanId)
        answer = readFreePageAnswer(response?.data)
      } catch {
        // No answer is a no: the ordinary path, and nothing to tell the person.
      }
      // A press refused meanwhile, or another scan's report by now: this is the older word.
      if (freePageQuestion.current !== question) return
      setFreePageAnswer({ scanId: claimedScanId, answer })
    })()
  }, [askFreePage, claimedScanId, active, portalMatched, signupFirst, canAsk])

  const putLive = useCallback(async (): Promise<'started' | 'plan_required' | 'refused'> => {
    if (!claimedScanId || !facts) return 'refused'
    setPutLiveError(null)
    setPutLiveStarting(true)
    startingRef.current = true
    notStartedStreak.current = 0
    if (jobTimer.current) {
      clearTimeout(jobTimer.current)
      jobTimer.current = null
    }
    // A scan that has a job is pressed for that job's own page, always. A first press is for the page the
    // person chose, even in the moment before its facts arrive, and else for the page the report shows.
    const ownPage = jobPage.current?.scanId === claimedScanId ? jobPage.current.url : null
    const chosenPage = chosen.current?.scanId === claimedScanId ? chosen.current.url : null
    try {
      const response = await apiService.startOnboardingPutLive(claimedScanId, ownPage ?? chosenPage ?? facts.url)
      const started = response.data ?? null
      /* A choice still in hand: seeing the job lets go of it and asks for the facts with no page. */
      const held = chosen.current?.scanId === claimedScanId
      setJob(started)
      if (started) sawJob(claimedScanId)
      /*
        The job is for a page the report does not show, and no choice is in
        hand to bring the report round: the press was for a page chosen
        whose read then failed (the failure let go of the choice), or for a
        job's own page when the report's pick has moved. The facts are asked
        for with no page, which the server answers with the job's page, so
        the screen names the page the fix is going on. The report is not
        taken away for a picture, and a sentence about a read that failed
        goes: the page is being read again.
      */
      if (started && !held && typeof started.url === 'string' && started.url && started.url !== facts.url) {
        waivedPicture.current = true
        setPutLiveError(null)
        askFacts.current?.()
      }
      if (started && !started.finishedAt) {
        jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
      }
      return 'started'
    } catch (error) {
      const { code, message, reason } = refusal(error)
      setJob(null)
      if (code === 'SUBSCRIPTION_REQUIRED' && ownPage === null && chosenPage !== null) {
        /*
          Refused for want of a plan, for a page chosen. A tab that still
          holds a choice can be behind: a try made elsewhere for another
          page of the scan, and stopped, is the account's to make again for
          nothing, and the server names no page in this refusal. So the
          scan's job is looked for, once. Found: it is taken in hand (the
          choice ends, and the report is asked for again about the job's
          page), and the person stays here with their try and its Try
          again, not sent to a trial. Not found, or no answer: there is no
          plan, as below.
        */
        const found = await apiService.getOnboardingPutLive(claimedScanId).then((response) => response.data ?? null).catch(() => null)
        if (found) {
          setJob(found)
          sawJob(claimedScanId)
          if (!found.finishedAt) jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
          setPutLiveError(COULD_NOT_START_SENTENCE)
          return 'refused'
        }
      }
      if (code === 'SUBSCRIPTION_REQUIRED') {
        /*
          No plan, and no free page either. After a card that said the page
          was free this is ordinary (another press won, the day's pages ran
          out), not a fault: the card stops saying free and is handed the
          server's reason, and nothing is asked again for this load.
        */
        freePageQuestion.current = { scanId: claimedScanId }
        setFreePageAnswer({ scanId: claimedScanId, answer: { eligible: false, reason } })
        setPutLiveError(START_TRIAL_SENTENCE)
        return 'plan_required'
      }
      setPutLiveError(message || COULD_NOT_START_SENTENCE)
      return 'refused'
    } finally {
      startingRef.current = false
      setPutLiveStarting(false)
    }
  }, [claimedScanId, facts, pollJob, sawJob, setJob])

  /*
    Another page of the scan, in place of the one the report picked. Only
    before any job, which the server holds to as well: what is checked here
    is read where the same tick can see it, so a choice made in the tick of
    a press, or of a job landing, is not taken.
  */
  const choosePage = useCallback((url: string) => {
    if (!claimedScanId || !active || !url) return
    if (jobLookedFor !== claimedScanId || jobSeen.current === claimedScanId || jobPage.current?.scanId === claimedScanId) return
    if (jobRef.current || startingRef.current) return
    /* One at a time, and never the page already shown. */
    if (choosing.current || url === facts?.url) return
    setPutLiveError(null)
    chosen.current = { scanId: claimedScanId, url }
    writeChosenPage(claimedScanId, url)
    choosing.current = url
    setChoosingPage(url)
    setChosenFor(claimedScanId)
    askFacts.current?.()
  }, [active, claimedScanId, facts?.url, jobLookedFor])

  const confirmAgain = useCallback(async () => {
    if (!claimedScanId) return
    setPutLiveError(null)
    setConfirming(true)
    notStartedStreak.current = 0
    if (jobTimer.current) {
      clearTimeout(jobTimer.current)
      jobTimer.current = null
    }
    try {
      const response = await apiService.confirmOnboardingPutLive(claimedScanId)
      const next = response.data ?? null
      setJob(next)
      if (next) sawJob(claimedScanId)
      if (next && !next.finishedAt) {
        jobTimer.current = setTimeout(() => void pollJob(), JOB_POLL_MS)
      }
    } catch (error) {
      setPutLiveError(refusal(error).message || COULD_NOT_CHECK_SENTENCE)
    } finally {
      setConfirming(false)
    }
  }, [claimedScanId, pollJob, sawJob, setJob])

  useEffect(() => () => { if (jobTimer.current) clearTimeout(jobTimer.current) }, [])

  const freePage = !askFreePage ? null : signupFirst === false ? NO_FREE_PAGE : freePageAnswer && freePageAnswer.scanId === claimedScanId ? freePageAnswer.answer : null
  /*
    An answer is still to come: everything the question needs is here or on
    its way, and there is no answer for this scan yet. Read from what the
    question itself waits on, never from `freePage` being null, which it
    also is wherever nothing is asked. Not known whether sign-up comes
    first, and the look for a job still out, both count as on its way: the
    card is already drawn by then, and the question follows as soon as they
    are back. Each of them ends, so this does.
  */
  const freePagePending = askFreePage && Boolean(claimedScanId) && active && portalMatched && signupFirst !== false && cardOnOffer && freePageAnswer?.scanId !== claimedScanId
  /* The look is marked for the scan it was made for, so another scan's report waits for its own. */
  const jobLookupSettled = Boolean(claimedScanId) && jobLookedFor === claimedScanId
  /* Before any job, and only then: the look on arrival is back with none, none has been seen since, and no press is on its way. */
  const canChoosePage = active && jobLookupSettled && jobSeenFor !== claimedScanId && job === null && !putLiveStarting
  /* Said for the scan the choice was made on, so another scan's report never reads it. */
  const pageChosen = Boolean(claimedScanId) && chosenFor === claimedScanId

  return { active, loading, awaitingPicture, pictureSlow, showWithoutPicture, facts, connection, job, putLiveError, putLiveStarting, jobLookupSettled, putLive, confirmAgain, confirming, refreshConnection, hubPages: hubPagesNow, hubPagesLoading, freePage, freePagePending, canChoosePage, choosePage, choosingPage, pageChosen }
}
